import { Rillet, SettingCardViewAction } from '@monetize/types/app';
import { ReactNode, useState } from 'react';
import {
  useGetRilletCoa,
  useUpdateAccountingConnectionStatus,
} from '../../../../../api/accountingService';
import { SettingCardEdit } from '../../../../../components/Settings/SettingCardEdit';
import { SettingCardView } from '../../../../../components/Settings/SettingCardView';
import { useToast } from '../../../../../services/toast';
import { RILLET_FORM_FIELDS } from './accounting.constants';
import { RilletForm } from './RilletForm';

interface RilletConnectionProps {
  connection: Rillet;
  description?: ReactNode;
}

export const RilletConnection = ({
  connection,
  description,
}: RilletConnectionProps) => {
  const { addToast } = useToast();
  const isEnabled = connection.enabled;
  const [mode, setMode] = useState<'VIEW' | 'EDIT'>('VIEW');

  const {
    data: coaData,
    isFetching: coaListFetching,
    refetch: refetchCoa,
  } = useGetRilletCoa();

  const { mutate: updateConnection, isPending: isProcessing } =
    useUpdateAccountingConnectionStatus({
      meta: { showErrorToast: true },
    });

  const handleRefetchCoa = async () => {
    const result = await refetchCoa();
    if (result.isError) {
      addToast({
        summary: 'Error',
        detail: 'Failed to refresh COA data. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleAction = (action: SettingCardViewAction) => {
    if (isProcessing) {
      return;
    }
    switch (action) {
      case 'ENABLE':
        updateConnection({ type: 'rillet', action: 'enable' });
        break;
      case 'DISABLE':
        updateConnection({ type: 'rillet', action: 'disable' });
        break;
      default:
        break;
    }
  };

  const paymentCoaName = connection.defaultPaymentAccountCode
    ? (() => {
        const selectedCoa = coaData?.paymentCoas?.find(
          (coa) => coa.code === connection.defaultPaymentAccountCode,
        );
        return selectedCoa
          ? `${selectedCoa.code} - ${selectedCoa.name}`
          : connection.defaultPaymentAccountCode;
      })()
    : 'Blank';

  return (
    <>
      {mode === 'VIEW' && (
        <SettingCardView
          status={isEnabled ? 'ACTIVE' : 'INACTIVE'}
          type="Rillet"
          isLoading={isProcessing || coaListFetching}
          isEnabled={isEnabled}
          actions={[
            {
              action: isEnabled ? 'DISABLE' : 'ENABLE',
              label: isEnabled ? 'Disable' : 'Enable',
              isDanger: isEnabled,
              ...(!isEnabled
                ? {}
                : {
                    confirmProps: {
                      title:
                        'Are you sure you want to disable this connection?',
                      description:
                        'This will temporarily deactivate the Rillet integration. You can re-enable it at any time.',
                    },
                  }),
            },
          ]}
          tags={[
            {
              label: RILLET_FORM_FIELDS.apiKey.label,
              value: connection.apiKey,
            },
            {
              label: RILLET_FORM_FIELDS.environment.label,
              value: connection.environment,
            },
            {
              label: RILLET_FORM_FIELDS.defaultPaymentAccountCode.label,
              tooltip: RILLET_FORM_FIELDS.defaultPaymentAccountCode.tooltip,
              value: paymentCoaName,
              colSpan: 1,
            },
          ]}
          onEdit={() => setMode('EDIT')}
          onAction={handleAction}
        />
      )}
      {mode === 'EDIT' && (
        <SettingCardEdit
          showCloseButton
          type="Rillet"
          handleCancel={() => setMode('VIEW')}
        >
          <RilletForm
            connection={connection}
            coaData={coaData}
            onClose={() => setMode('VIEW')}
            refetchCoaData={handleRefetchCoa}
            isRefetchingCoa={coaListFetching}
          />
        </SettingCardEdit>
      )}
    </>
  );
};
